﻿import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../../utilities/crud-functions';
import { ResponseError } from '../../models/response-error';
import { CoaComponent } from '../../models/coa-component';
import { AttachedImage } from '../../models/attached-image';
import { FullCoa } from '../../models/full-coa';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';

import DataGrid, {
    Column,
    Selection,
    Toolbar,
    Item as ToolBarItem,
    LoadPanel,
    Paging,
} from 'devextreme-react/data-grid';

import TextBox from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';

export interface Props {
    benchSheetId?: string;
    batchId?: string;
    onError?: (error: ResponseError) => void;
}

export default function CoaGcInformation(props: Props) {
    const [fullCoa, setFullCoa] =
        useState<FullCoa>();
        
    const [molecularImage, setMolecularImage] =
        useState<AttachedImage>();
        
    const [coaImage, setCoaImage] =
        useState<AttachedImage>();
        
    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        generateCofAData();
    }, []);

    const fullCoaCommonData = fullCoa && fullCoa.commonData ? fullCoa.commonData : null;
    const gasChromatographyData = fullCoa && fullCoa.gasChromatographyData ? fullCoa.gasChromatographyData : null;
    const fcColumnData = fullCoa && fullCoa.column ? fullCoa.column : null;
    const fcTemperatureProgram = fullCoa && fullCoa.temperatureProgram ? fullCoa.temperatureProgram : null;

    return (
        <div>
            <div className="pw-content">
                <div className="lq-header pw-margin-top-05 pw-bg-green">
                    <div className="lq-header-title">GC C of A Information</div>
                </div>
            </div>

            <>
                {!fullCoaCommonData &&
                    <div className="pw-centered-empty-content">No data</div>
                }
            </>

            <>
                {fullCoaCommonData &&
                    <div className="pw-content">
                        <div className="tab-subtitle pw-blue">Common Data</div>
                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Part Number</div>
                                <TextBox id="catalogNumber" className="textbox"
                                    placeholder="part number" value={fullCoaCommonData ? fullCoaCommonData.partNumber : ''} />
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Lot Number</div>
                                <TextBox id="batchName" className="textbox"
                                    placeholder="batch name" value={fullCoaCommonData ? fullCoaCommonData.batchName : ''} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Expiry Date</div>
                                <TextBox id="coaExpiryDate" className="textbox"
                                    placeholder="expiry date " value={fullCoaCommonData ? fullCoaCommonData.coaExpiryDate : ''} />
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Manufacturing Date</div>
                                <TextBox id="coaProductionDate" className="textbox"
                                    placeholder="manufacturing date" value={fullCoaCommonData ? fullCoaCommonData.coaProductionDate : ''} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-30">
                                <div className="pw-form-unit-label">Name Label</div>
                                <TextBox id="nameLabel" className="textbox"
                                    placeholder="Catalog number" value={fullCoaCommonData ? fullCoaCommonData.nameLabel : ''} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-30">
                                <div className="pw-form-unit-label">Long Name</div>
                                <TextBox id="longName" className="textbox"
                                    placeholder="batch name" value={fullCoaCommonData ? fullCoaCommonData.longName : ''} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-30">
                                <div className="pw-form-unit-label">Main Header</div>
                                <TextBox id="mainHeader" className="textbox"
                                    placeholder="batch name" value={fullCoaCommonData ? fullCoaCommonData.mainHeader : ''} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Storage</div>
                                <TextBox id="storage" className="textbox"
                                    placeholder="storage" value={fullCoaCommonData ? fullCoaCommonData.storage : ''} />
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Solvent/Matrix</div>
                                <TextBox id="solvent" className="textbox"
                                    placeholder="solvent" value={fullCoaCommonData ? fullCoaCommonData.solvent : ''} />
                            </div>
                        </div>

                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Packaging Description</div>
                            <TextBox id="packagingDescription" className="textbox"
                                placeholder="packaging description" value={fullCoaCommonData ? fullCoaCommonData.packagingDescription : ''} />
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Reported Value Caption</div>
                                <TextBox id="reportedValueName" className="textbox"
                                    placeholder="reported value" value={fullCoaCommonData ? fullCoaCommonData.reportedValueName : ''} />
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Reference</div>
                                <TextBox id="methodReference" className="textbox"
                                    placeholder="reference" value={fullCoaCommonData ? fullCoaCommonData.methodReference : ''} />
                            </div>
                        </div>
                    </div>
                }
            </>

            <>
                {fcColumnData &&
                    <div className="pw-content">
                        <div className="tab-subtitle pw-blue">Column Data</div>

                        <div className="pw-form-unit">
                            <label className="textbox-label">Column Information String</label>
                            <TextBox id="columnInformation" className="textbox"
                                placeholder="column information" value={fcColumnData ? fcColumnData.columnInformation : ''} />
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">Description</div>
                                <TextBox id="reportedValueName" className="textbox"
                                    placeholder="technology name" value={fcColumnData ? fcColumnData.columnHeaderDescription : ''} />
                            </div>

                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">Length</div>
                                <NumberBox id="methodReference" className="textbox"
                                    placeholder="reference" value={fcColumnData ? fcColumnData.columnHeaderLength : undefined} />
                            </div>

                            <div className="pw-form-unit pw-comp-5">
                                <div className="pw-form-unit-label">UOM</div>
                                <TextBox id="methodReference" className="textbox"
                                    placeholder="reference" value={fcColumnData ? fcColumnData.lengthUomName : ''} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">Diameter</div>
                                <NumberBox id="reportedValueName" className="textbox"
                                    placeholder="technology name" value={fcColumnData ? fcColumnData.columnHeaderDiameter : undefined} />
                            </div>

                            <div className="pw-form-unit pw-comp-5">
                                <div className="pw-form-unit-label">UOM</div>
                                <TextBox id="methodReference" className="textbox"
                                    placeholder="reference" value={fcColumnData ? fcColumnData.diameterUomName : ''} />
                            </div>

                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">Film Thickness</div>
                                <NumberBox id="methodReference" className="textbox"
                                    placeholder="reference" value={fcColumnData ? fcColumnData.columnHeaderFilmThickness : undefined} />
                            </div>

                            <div className="pw-form-unit pw-comp-5">
                                <div className="pw-form-unit-label">UOM</div>
                                <TextBox id="methodReference" className="textbox"
                                    placeholder="reference" value={fcColumnData ? fcColumnData.filmThicknessUomName : ''} />
                            </div>
                        </div>
                    </div>
                }
            </>

            <>
                {fcTemperatureProgram &&
                    <div className="pw-content">
                        <div className="tab-subtitle pw-blue">Temperature Program</div>

                        <div className="pw-form-unit">
                            <label className="textbox-label">Temperature Program String</label>
                            <TextBox id="columnInformation" className="textbox"
                                placeholder="column information" value={fcTemperatureProgram ? fcTemperatureProgram.temperatureProgramInformation : ''} />
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">Init Temperature</div>
                                <NumberBox id="reportedValueName" className="textbox"
                                    placeholder="technology name" value={fcTemperatureProgram ? fcTemperatureProgram.initTemperature : undefined} />
                            </div>
                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">TimeIncrement #1</div>
                                <NumberBox id="reportedValueName" className="textbox"
                                    placeholder="technology name" value={fcTemperatureProgram ? fcTemperatureProgram.timeIncrement1 : undefined} />
                            </div>
                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">Second Temperature</div>
                                <NumberBox id="reportedValueName" className="textbox"
                                    placeholder="technology name" value={fcTemperatureProgram ? fcTemperatureProgram.secondTemperature : undefined} />
                            </div>
                        </div>
                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">Time Increment 2</div>
                                <NumberBox id="reportedValueName" className="textbox"
                                    placeholder="technology name" value={fcTemperatureProgram ? fcTemperatureProgram.timeIncrement2 : undefined} />
                            </div>
                            <div className="pw-form-unit pw-comp-10">
                                <div className="pw-form-unit-label">Rate</div>
                                <NumberBox id="reportedValueName" className="textbox"
                                    placeholder="technology name" value={fcTemperatureProgram ? fcTemperatureProgram.rate : undefined} />
                            </div>
                        </div>
                    </div>
                }
            </>

            <>
                {gasChromatographyData &&
                    <div className="pw-content">
                        <div className="tab-subtitle pw-blue">Gas Chromatography Data </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Carrier Gas</div>
                                <TextBox id="carrierGas" className="textbox"
                                    placeholder="carrier gas" value={gasChromatographyData ? gasChromatographyData.carrierGas : ''} />
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Column Flow Rate</div>
                                <NumberBox id="columnFlowRate" className="textbox"
                                    placeholder="solvent" value={gasChromatographyData ? gasChromatographyData.columnFlowRate : undefined} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Inlet Temperature</div>
                                <NumberBox id="inletTemperature" className="textbox"
                                    placeholder="carrier gas" value={gasChromatographyData ? gasChromatographyData.inletTemperature : undefined} />
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Injection Volume</div>
                                <NumberBox id="injectionVolume" className="textbox"
                                    placeholder="injection volume" value={gasChromatographyData ? gasChromatographyData.injectionVolume : undefined} />
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Injection Volume UOM</div>
                                <TextBox id="injectionVolumeUom" className="textbox"
                                    placeholder="uom" value={gasChromatographyData ? gasChromatographyData.injectionVolumeUom : undefined} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">GC Inlet Mode</div>
                                <TextBox id="gcInletModeName" className="textbox"
                                    placeholder="gc inlet mode" value={gasChromatographyData ? gasChromatographyData.gcInletModeName : ''} />
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">GC Inlet Type</div>
                                <TextBox id="gcInletTypeName" className="textbox"
                                    placeholder="gc inlet type" value={gasChromatographyData ? gasChromatographyData.gcInletTypeName : ''} />
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Split Ratio</div>
                                <TextBox id="splitRatio" className="textbox"
                                    placeholder="split ratio" value={gasChromatographyData ? gasChromatographyData.splitRatio : ''} />
                            </div>
                        </div>
                    </div>
                }
            </>

        </div>
    );

    async function generateCofAData() {
        if (props.batchId) {
            const batchId = props.batchId;
            
            await Promise.all([
                getFullCoaData(batchId, props.benchSheetId)
                    .then(data => {
                        setFullCoa(data);
                    })
                    .catch((error: ResponseError) => {
                        if (props.onError)
                            props.onError(error);
                })
            ])
        }
        else {
            const title = "Data entry missing";
            const message = "You must first select a batch and at least one bench sheet";

            showInformationMessage(ModalMessageType.Information, title, message);
        }
    }

    async function getFullCoaData(batchId: string, benchSheetId: string) {
        return await CrudFunctions.getData(`coa/fullcoa/${batchId}/${benchSheetId}`);
    }

    function showInformationMessage(mode: ModalMessageType, title: string, message: string) {
        let mm: ModalMetadata = {
            error: null,
            mode: mode,
            title: title,
            message: message
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
